import type { Finding } from './shared';

/**
 * Menu links that need attention ("tinycms UX optimization review v13", Pages
 * → Header): links to a section (collection) with no published articles yet,
 * and links to an address with nothing live at all. The server reports them
 * on the site document (siteDocument.js checkSiteLinks: `empty_section`, a
 * warning; `broken_internal_link`, a warning too since c4b822db) with the link's
 * href and words.
 */

export type NavPart = 'header' | 'footer';

const NAV_PATH = /^(?:translations\.[^.]+\.)?navigation\.(header|cta|footer|footerColumns)\b/;

export interface NavIssue {
  key: string;
  part: NavPart;
  /** empty: a section with nothing published; missing: nothing live at that address. */
  kind: 'empty' | 'missing';
  /** Missing, but a page that isn't live yet is there: it goes live with the site. */
  notLive?: boolean;
  label: string;
  href: string;
  collection: string | null;
}

/** A finding about a menu link (rather than, say, the home page or contrast). */
export const isMenuLinkFinding = (f: Finding) =>
  (f.code === 'empty_section' || f.code === 'broken_internal_link') &&
  Boolean(f.href) &&
  NAV_PATH.test(f.path || '');

/** "/visit/?x" → "visit": how a page's address reads in draftPaths. */
const pathOf = (href: string) => href.replace(/[?#].*$/, '').replace(/^\/+|\/+$/g, '');

/**
 * One row per link address and part (a translation's copy of a link isn't a
 * second row). `draftPaths`: addresses of pages that exist but aren't live
 * (a new site's template pages), so their links say "not live yet", not
 * "goes nowhere" (backlog bfbfa5d7).
 */
export function navIssues(
  findings: Finding[] | null | undefined,
  { draftPaths }: { draftPaths?: Set<string> } = {}
): NavIssue[] {
  const seen = new Set<string>();
  const out: NavIssue[] = [];
  for (const f of findings || []) {
    const m = isMenuLinkFinding(f) ? NAV_PATH.exec(f.path || '') : null;
    if (!m || !f.href) continue;
    const part: NavPart = m[1].startsWith('footer') ? 'footer' : 'header';
    const kind = f.code === 'empty_section' ? 'empty' : 'missing';
    const key = `${part}:${kind}:${f.href}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      part,
      kind,
      label: f.linkLabel || f.href,
      href: f.href,
      collection: f.collection || null,
      ...(kind === 'missing' && draftPaths?.has(pathOf(f.href)) ? { notLive: true } : {}),
    });
  }
  return out;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Content → Design's one line about them, in place of the bare findings. */
export function navSummary(issues: NavIssue[]): string {
  const empty = issues.filter((i) => i.kind === 'empty').length;
  const notLive = issues.filter((i) => i.notLive).length;
  const missing = issues.length - empty - notLive;
  const emptyWords = (n: number) =>
    `${n === 1 ? 'goes to a section' : 'go to sections'} with no published articles yet`;
  const missingWords = (n: number) =>
    n === 1 ? 'goes to a page that doesn’t exist' : 'go to pages that don’t exist';
  const notLiveWords = (n: number) =>
    n === 1 ? 'goes to a page that isn’t live yet' : 'go to pages that aren’t live yet';
  const parts = [
    empty ? `${empty} ${emptyWords(empty)}` : '',
    notLive ? `${notLive} ${notLiveWords(notLive)}` : '',
    missing ? `${missing} ${missingWords(missing)}` : '',
  ].filter(Boolean);
  if (parts.length > 1) {
    return `${plural(issues.length, 'menu link')} need attention: ${parts.join(', ')}.`;
  }
  const n = empty || notLive || missing;
  const words = empty ? emptyWords(n) : notLive ? notLiveWords(n) : missingWords(n);
  return `${plural(n, 'menu link')} ${words}.`;
}

/** The Pages rail's Header / Footer line: "· 2 go to empty sections". */
export function railNote(issues: NavIssue[], part: NavPart): string {
  const mine = issues.filter((i) => i.part === part);
  const empty = mine.filter((i) => i.kind === 'empty').length;
  const notLive = mine.filter((i) => i.notLive).length;
  const missing = mine.length - empty - notLive;
  return [
    empty
      ? ` · ${empty} ${empty === 1 ? 'goes' : 'go'} to ${empty === 1 ? 'an empty section' : 'empty sections'}`
      : '',
    notLive ? ` · ${notLive} not live yet` : '',
    missing ? ` · ${missing} ${missing === 1 ? 'goes' : 'go'} nowhere` : '',
  ].join('');
}

interface Link {
  label: string;
  href?: string | null;
  hideWhenEmpty?: boolean;
  children?: Link[];
  [k: string]: unknown;
}
interface Nav {
  header?: Link[];
  footer?: Link[];
  footerColumns?: Array<{ links?: Link[]; [k: string]: unknown }>;
  [k: string]: unknown;
}

/** Same address, ignoring a trailing slash. */
const sameHref = (a?: string | null, b?: string | null) =>
  Boolean(a && b) && a!.replace(/\/+$/, '') === b!.replace(/\/+$/, '');

/**
 * The navigation patch for "Hide until it has articles" (hide: true) and its
 * Undo (hide: false): every link to `href` in the header, its dropdowns, the
 * footer and the footer columns. Only the lists that change are sent; the
 * server replaces each list it is given.
 */
export function hidePatch(nav: Nav | null | undefined, href: string, hide: boolean) {
  const flag = (l: Link): Link => {
    if (!sameHref(l.href, href)) return l;
    const { hideWhenEmpty: _drop, ...rest } = l;
    return hide ? { ...rest, hideWhenEmpty: true } : rest;
  };
  const header = (nav?.header || []).map((l) =>
    l.children ? { ...flag(l), children: l.children.map(flag) } : flag(l)
  );
  const footer = (nav?.footer || []).map(flag);
  const footerColumns = (nav?.footerColumns || []).map((c) => ({
    ...c,
    links: (c.links || []).map(flag),
  }));
  const patch: Nav = {};
  if (JSON.stringify(header) !== JSON.stringify(nav?.header || [])) patch.header = header;
  if (JSON.stringify(footer) !== JSON.stringify(nav?.footer || [])) patch.footer = footer;
  if (JSON.stringify(footerColumns) !== JSON.stringify(nav?.footerColumns || []))
    patch.footerColumns = footerColumns;
  return patch;
}

/**
 * The navigation patch that removes every link to `href` (a link to nothing).
 * A dropdown left with no entries and no link of its own goes too.
 */
export function removePatch(nav: Nav | null | undefined, href: string) {
  const keep = (l: Link) => !sameHref(l.href, href);
  const header = (nav?.header || [])
    .filter(keep)
    .map((l) => (l.children ? { ...l, children: l.children.filter(keep) } : l))
    .filter((l) => l.href || l.children?.length);
  const footer = (nav?.footer || []).filter(keep);
  const footerColumns = (nav?.footerColumns || [])
    .map((c) => ({ ...c, links: (c.links || []).filter(keep) }))
    .filter((c) => c.links.length);
  const patch: Nav = {};
  if (JSON.stringify(header) !== JSON.stringify(nav?.header || [])) patch.header = header;
  if (footer.length !== (nav?.footer || []).length) patch.footer = footer;
  if (JSON.stringify(footerColumns) !== JSON.stringify(nav?.footerColumns || []))
    patch.footerColumns = footerColumns;
  return patch;
}

/** What to ask the agent for "Assign articles": it picks; the picks come back for approval. */
export function assignPrompt(issue: NavIssue): string {
  const collection = issue.collection || issue.label;
  return `The “${issue.label}” link in my site’s ${issue.part} goes to the ${issue.label} section (${issue.href}), which has no published articles yet. Find the live articles that belong in it and add “${collection}” to each one’s collections. Don’t publish them: leave them waiting so they come to Today in tinycms, where I’ll approve them.`;
}

/** tinycmsToday's menuLinks: what readers of the live site hit (today.js menuLinks). */
export interface TodayMenuLinks {
  headerLinks: number;
  footerLinks: number;
  issues: Array<Pick<NavIssue, 'part' | 'kind' | 'href' | 'label' | 'collection'>>;
}

/**
 * Today's notice: "4 of 6 header links go to empty sections, so readers see
 * “Nothing published here yet”." and the way to Pages → Header, or null.
 */
export function todayMenuNotice(menu: TodayMenuLinks | null | undefined) {
  const issues = menu?.issues || [];
  if (!issues.length) return null;
  const part: NavPart = issues.some((i) => i.part === 'header') ? 'header' : 'footer';
  const mine = issues.filter((i) => i.part === part);
  const total = part === 'header' ? menu!.headerLinks : menu!.footerLinks;
  const empty = mine.filter((i) => i.kind === 'empty').length;
  const missing = mine.length - empty;
  const of = (n: number) => `${n} of ${plural(Math.max(total, n), `${part} link`)}`;
  const lines = [
    empty
      ? `${of(empty)} ${empty === 1 ? 'goes to an empty section' : 'go to empty sections'}, so readers see “Nothing published here yet”.`
      : '',
    missing
      ? `${of(missing)} ${missing === 1 ? 'goes to a page that doesn’t exist' : 'go to pages that don’t exist'}.`
      : '',
    issues.length > mine.length
      ? `The ${part === 'header' ? 'footer' : 'header'} has links to check too.`
      : '',
  ];
  return {
    text: lines.filter(Boolean).join(' '),
    label: `Review in Pages › ${part === 'header' ? 'Header' : 'Footer'} →`,
    to: `/content?view=pages&page=${part}`,
  };
}
